import type {
  Image,
  LocaleString,
  Story,
  StoryQuery,
  StoryQueryVariables,
  AllStorySlugsQuery,
  AllStorySlugsQueryVariables,
  AllPagesQuery,
} from '@/.generated/sanityTypes/graphql'
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next/types'
import React from 'react'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import Layout from '@/components/layout'
import BlockContent from '@/components/blockContent'
import Card from '@/components/card'
import { BlackButtonLink } from '@/components/buttons'
import withNewLines from '@/lib/withNewLines'
import { YellowTag } from '@/components/tags'
import { getTypedLocale, isLocalString } from '../../lib/typeHelper'
import { request } from '@/lib/sanityClient'
import {
  story as storyQuery,
  allStorySlugs as allStorySlugsQuery,
} from './[slug].sanity.graphql'
import { getAllPages, getPageQueryVariables } from '@/lib/query'

const Introduction = ({ story }: { story: Story }) => {
  const locale = getTypedLocale(useRouter().locale)

  return (
    <div className="mx-auto w-full max-w-screen-xl px-5">
      <div className="bg-white">
        <div className="xs:pb-12 mx-auto mb-5 max-w-5xl px-4 pb-8 pt-4 text-center md:px-12 md:pt-8 lg:pb-16">
          {story.storyTags && story.storyTags.length > 0 ? (
            <YellowTag>{story.storyTags[0]?.title?.[locale]}</YellowTag>
          ) : null}
          {story.title?.[locale] && (
            <h1 className="xs:text-4xl mt-4 text-3xl leading-none text-red-500 md:mt-6 md:text-5xl lg:mt-8 lg:text-6xl">
              {withNewLines(story.title[locale])}
            </h1>
          )}
          {story.subtitle?.[locale] && (
            <p className="xs:text-4xl text-3xl leading-none text-black md:text-5xl lg:text-6xl">
              {withNewLines(story.subtitle[locale])}
            </p>
          )}
          <div className="xs:text-2xl text-slightly-red-gray mx-auto mt-4 max-w-xl text-center text-xl leading-tight md:max-w-2xl md:text-2xl md:leading-snug">
            {story.intro && story.intro[locale]}
          </div>
        </div>
      </div>
    </div>
  )
}

type StoryPageProps = {
  data: StoryQuery
  messages: unknown
  isPreview: boolean
  allPages: AllPagesQuery
}

const StoryPage = ({ data, isPreview, allPages }: StoryPageProps) => {
  const router = useRouter()
  const { locale = 'de' } = router
  const story = data?.allStory[0]
  const t = useTranslations('stories')

  if (router.isFallback) {
    return <div>Loading...</div>
  }

  return (
    <Layout
      allPages={allPages}
      isPreview={isPreview}
      title={
        (isLocalString(story?.title) &&
          story?.title[locale as keyof LocaleString]) ||
        undefined
      }
      headerFiles={(story?.image ? [story.image] : []) as Image[]}
    >
      <Introduction story={story as Story}></Introduction>

      {story?.richTextSections &&
        story.richTextSections.map((section: any) => {
          return (
            <Card key={section._key} className="mx-auto max-w-3xl md:text-lg">
              <BlockContent blocks={section![`${locale}Raw`]} />
            </Card>
          )
        })}

      <div className="text-center">
        <BlackButtonLink href={`/stories`}>{t('overview')}</BlackButtonLink>
      </div>
    </Layout>
  )
}

export async function getStaticPaths() {
  const pageData = await request<
    AllStorySlugsQuery,
    AllStorySlugsQueryVariables
  >(allStorySlugsQuery)
  const slugs = pageData.data.allStory.map((story) => story.slug?.current)

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: true,
  }
}

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<StoryPageProps>> {
  if (!context.params?.slug || Array.isArray(context.params)) {
    throw new Error(`slug is not a string, slug is: '${context.params?.slug}'`)
  }
  const isPreview = context.preview || false
  const queryVariables = getPageQueryVariables(
    context.params.slug.toString()!,
    'slug',
  )

  const pageData = await request<StoryQuery, StoryQueryVariables>(
    storyQuery,
    queryVariables,
    isPreview,
  )

  if (pageData.errors)
    throw new Error(`graphql Error occured: ${pageData.errors[0].message}`)

  if (!pageData.data.allStory?.length) return { notFound: true }

  return {
    props: {
      data: pageData.data,
      messages: (await import(`@/translation/${context.locale}.json`)).default,
      isPreview,
      allPages: await getAllPages(),
    },
  }
}

export default StoryPage
